import { BranchId } from './catalog';

/**
 * The tutor roster. Tutors are current employees at the firms candidates
 * are applying to, so they appear under initials only: no names, no
 * photos. Cards stay deliberately simple: seat plus one line of
 * experience, nothing about what they do day-to-day.
 *
 * Each tutor gets their own calendar. Paste each tutor's real Calendly
 * event link into TUTOR_CALENDARS below (see SETUP.md; suggested slugs
 * are listed there). While an entry is empty, that tutor's bookings fall
 * back to the shared calendar, tagged with the tutor's initials so the
 * session still reaches the right person.
 */

export interface Tutor {
  id: string;
  initials: string;
  branch: BranchId;
  role: string;
  seat: string;
  facts: string[];
  calendlyUrl: string;
}

const SHARED_CALENDLY = 'https://calendly.com/hello-bulgebracket/30min';

/**
 * Per-business-line Calendly event links. Create one event type per track
 * in the Calendly dashboard (suggested slugs: st-session, ib-session,
 * consulting-session) and paste the links here. Empty string = fall back
 * to the shared calendar. Tutor-specific links (below) win over these.
 */
export const BRANCH_CALENDARS: Record<BranchId, string> = {
  'sales-and-trading': '',
  'investment-banking': '',
  consulting: ''
};

/** Per-tutor Calendly event links. Empty string = branch/shared fallback. */
export const TUTOR_CALENDARS: Record<string, string> = {
  jm: '',
  ak: '',
  rs: '',
  tb: '',
  dl: '',
  ef: '',
  cd: '',
  cm: '',
  vg: '',
  xd: '',
  kr: '',
  dt: '',
  sv: '',
  hw: '',
  lc: '',
  pn: '',
  fo: ''
};

/**
 * The calendar a tutor's bookings actually go to: their own link first,
 * then the business line's calendar, then the shared fallback (which
 * gets tagged with the tutor's initials by the caller).
 */
export const calendarFor = (tutor: Tutor): { url: string; shared: boolean } => {
  const own = TUTOR_CALENDARS[tutor.id];
  if (own) return { url: own, shared: false };
  const branchUrl = BRANCH_CALENDARS[tutor.branch];
  if (branchUrl) return { url: branchUrl, shared: false };
  return { url: SHARED_CALENDLY, shared: true };
};

export const TUTORS: Tutor[] = [
  {
    id: 'jm',
    initials: 'JM',
    branch: 'sales-and-trading',
    role: 'FX Options Trader',
    seat: 'Bulge-bracket bank',
    facts: ['Fourth year on a G10 FXO desk'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'ak',
    initials: 'AK',
    branch: 'sales-and-trading',
    role: 'Equities Sales-Trader',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in cash equities'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'rs',
    initials: 'RS',
    branch: 'sales-and-trading',
    role: 'Rates Trader',
    seat: 'Global investment bank',
    facts: ['Experienced in G10 rates'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'tb',
    initials: 'TB',
    branch: 'sales-and-trading',
    role: 'Macro Trader',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in macro'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'dl',
    initials: 'DL',
    branch: 'sales-and-trading',
    role: 'Delta One Trader',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in delta one'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'ef',
    initials: 'EF',
    branch: 'sales-and-trading',
    role: 'EM FX Trader',
    seat: 'Global investment bank',
    facts: ['Experienced in EM FX'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'cd',
    initials: 'CD',
    branch: 'sales-and-trading',
    role: 'Credit Trader',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in flow credit'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'cm',
    initials: 'CM',
    branch: 'sales-and-trading',
    role: 'Commodities Trader',
    seat: 'Global investment bank',
    facts: ['Experienced in commodities'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'vg',
    initials: 'VG',
    branch: 'sales-and-trading',
    role: 'Equity Derivatives Trader',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in equity derivatives'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'xd',
    initials: 'XD',
    branch: 'sales-and-trading',
    role: 'Digital Assets Trader',
    seat: 'Global investment bank',
    facts: ['Experienced in crypto'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'kr',
    initials: 'KR',
    branch: 'sales-and-trading',
    role: 'Portfolio Manager',
    seat: 'Multi-strategy hedge fund',
    facts: ['Experienced in fixed income RV'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'dt',
    initials: 'DT',
    branch: 'investment-banking',
    role: 'M&A Associate',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in M&A'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'sv',
    initials: 'SV',
    branch: 'investment-banking',
    role: 'Leveraged Finance Analyst',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in leveraged finance'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'hw',
    initials: 'HW',
    branch: 'investment-banking',
    role: 'ECM Associate',
    seat: 'Bulge-bracket bank',
    facts: ['Experienced in equity capital markets'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'lc',
    initials: 'LC',
    branch: 'consulting',
    role: 'Consultant',
    seat: 'Top-three strategy firm',
    facts: ['Experienced in growth strategy'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'pn',
    initials: 'PN',
    branch: 'consulting',
    role: 'Strategy Consultant',
    seat: 'Big Four strategy arm',
    facts: ['Experienced in commercial due diligence'],
    calendlyUrl: SHARED_CALENDLY
  },
  {
    id: 'fo',
    initials: 'FO',
    branch: 'consulting',
    role: 'Engagement Manager',
    seat: 'Top-three strategy firm',
    facts: ['Experienced in operations and transformation'],
    calendlyUrl: SHARED_CALENDLY
  }
];

export const tutorsForBranch = (branch: BranchId): Tutor[] =>
  TUTORS.filter(tutor => tutor.branch === branch);
